/**
 * Internationalization Configuration
 * ============================================================================
 *
 * Purpose:
 * --------
 * Configures i18next for multi-language support across the application.
 *
 * This module is responsible for:
 * - Initializing i18next with language detection and lazy loading
 * - Defining supported languages and namespaces
 * - Setting up fallback chains and default configurations
 * - Managing translation resource loading strategies
 *
 * Design principles:
 * ------------------
 * - Language detection follows: localStorage → browser → default (English)
 * - Translations are lazy-loaded per namespace to optimize bundle size
 * - Fallback chain ensures graceful degradation (sw → en)
 * - All user-facing text must flow through the translation layer
 *
 * Non-goals:
 * ----------
 * - Performing actual translations or text rendering
 * - Managing user language preferences (handled by settings)
 * - Currency or date formatting (separate utilities handle these)
 *
 * Supported languages:
 * --------------------
 * - en: English (default, fallback)
 * - sw: Swahili (Tanzania, Kenya, Uganda)
 *
 * Changes to this file affect language behavior across the entire
 * application and should be reviewed carefully.
 * ============================================================================
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

/**
 * Supported languages
 *
 * en: English (default)
 * sw: Swahili (East Africa)
 */
export const SUPPORTED_LANGUAGES = ['en', 'sw'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Translation namespaces
 *
 * Organized by feature domain to enable lazy loading
 * and maintainable translation files.
 */
export const NAMESPACES = [
  'common', // Shared UI elements, buttons, labels
  'auth', // Authentication and registration
  'policies', // Policy management
  'claims', // Claims processing
  'validation', // Form validation messages
  'api-errors', // API error messages and titles
  'errors', // Error handling messages
  'notifications', // Notification messages
  'navigation', // Navigation and layout labels
  'settings', // Settings and preferences
  'help', // Help center and support
  'users', // Users and team management
  'customers', // Customer management
  'agents', // Agent management
  'branches', // Branch management
  'payments', // Payment management
  'products', // Product management
] as const;

export type TranslationNamespace = (typeof NAMESPACES)[number];

/**
 * Storage key for persisting user language preference
 */
const LANGUAGE_STORAGE_KEY = 'bimanova_language';

/**
 * Get initial language from user preferences store (Zustand)
 * Falls back to localStorage if store not available
 */
const getInitialLanguage = (): string | undefined => {
  try {
    const storedPrefs = localStorage.getItem('user-preferences-storage');
    if (storedPrefs) {
      const parsed = JSON.parse(storedPrefs);
      if (parsed?.state?.language) {
        return parsed.state.language;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return undefined;
};

/**
 * Initialize i18next
 *
 * Configured with:
 * - HTTP backend for lazy-loading translation files
 * - Browser language detection with localStorage persistence
 * - React integration for hooks and components
 */
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Default language when detection fails
    fallbackLng: 'en',

    // Fallback chain for missing translations
    // sw → en (ensures English is ultimate fallback)
    fallbackNS: 'common',

    // Default namespace loaded on init
    defaultNS: 'common',

    // All available namespaces
    ns: NAMESPACES,

    // Supported languages
    supportedLngs: SUPPORTED_LANGUAGES,

    // Development features
    debug: import.meta.env.DEV,

    // Interpolation config
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Backend configuration for loading translation files
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      // Don't load all languages upfront - lazy load on demand
      requestOptions: {
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'default',
      },
    },

    // Language detection configuration
    detection: {
      // Detection order: custom lookup → localStorage → navigator language → default
      order: ['localStorage', 'navigator'],

      // Cache user preference in localStorage
      caches: ['localStorage'],

      // Storage key for language preference
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,

      // Don't detect from cookie, querystring, etc.
      lookupCookie: undefined,
      lookupQuerystring: undefined,
      lookupSessionStorage: undefined,
    },

    // React-specific options
    react: {
      useSuspense: true, // Use React.Suspense for async loading
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
  });

// Sync initial language from Zustand store if available
const initialLang = getInitialLanguage();
if (initialLang && initialLang !== i18n.language && SUPPORTED_LANGUAGES.includes(initialLang as SupportedLanguage)) {
  i18n.changeLanguage(initialLang);
}

export default i18n;
